import { alpha, createTheme } from "@mui/material/styles";

export const engineTokens = {
  brand: "#F58220",
  brandHover: "#E8722E",
  brandSoft: "#FFF1E0",
  brandSofter: "#FFF8F0",

  slate50: "#F8FAFC",
  slate100: "#F1F5F9",
  slate200: "#E5E7EB",
  slate300: "#CBD5E1",
  slate400: "#94A3B8",
  slate500: "#64748B",
  slate600: "#475569",
  slate700: "#334155",
  slate900: "#0F172A",

  success: "#16A34A",
  warning: "#F59E0B",
  info: "#0EA5E9",
  danger: "#DC2626",

  bgPage: "#FAFAFA",
  bgPaper: "#FFFFFF",
  bgMuted: "#F8FAFC",
  border: "#E5E7EB",
  borderStrong: "#CBD5E1",

  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",

  radiusSm: 6,
  radiusMd: 8,
  radiusLg: 10,
};

export const engineTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: engineTokens.brand,
      dark: engineTokens.brandHover,
      light: engineTokens.brandSoft,
      contrastText: "#FFFFFF",
    },
    success: { main: engineTokens.success },
    warning: { main: engineTokens.warning },
    info: { main: engineTokens.info },
    error: { main: engineTokens.danger },
    background: {
      default: engineTokens.bgPage,
      paper: engineTokens.bgPaper,
    },
    text: {
      primary: engineTokens.textPrimary,
      secondary: engineTokens.textSecondary,
      disabled: engineTokens.textMuted,
    },
    divider: engineTokens.border,
    action: {
      hover: engineTokens.slate50,
      selected: engineTokens.slate100,
    },
  },
  shape: {
    borderRadius: engineTokens.radiusMd,
  },
  typography: {
    button: {
      textTransform: "none",
      fontWeight: 500,
      letterSpacing: 0,
    },
    subtitle2: {
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: engineTokens.border,
          borderRadius: engineTokens.radiusLg,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: engineTokens.radiusMd,
          fontWeight: 500,
        },
        sizeSmall: {
          minHeight: 30,
          paddingTop: 5,
          paddingBottom: 5,
        },
        containedPrimary: {
          backgroundColor: engineTokens.brand,
          color: "#FFFFFF",
          "&:hover": { backgroundColor: engineTokens.brandHover },
        },
        outlined: {
          borderColor: engineTokens.border,
          color: engineTokens.textPrimary,
          "&:hover": {
            borderColor: engineTokens.borderStrong,
            backgroundColor: engineTokens.slate50,
          },
        },
        outlinedPrimary: {
          borderColor: engineTokens.brand,
          color: engineTokens.brand,
          "&:hover": {
            borderColor: engineTokens.brandHover,
            backgroundColor: engineTokens.brandSofter,
          },
        },
        textPrimary: {
          color: engineTokens.brand,
          "&:hover": {
            backgroundColor: engineTokens.brandSofter,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: engineTokens.radiusSm,
          color: engineTokens.textSecondary,
          "&:hover": {
            backgroundColor: engineTokens.slate100,
            color: engineTokens.textPrimary,
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: { color: "primary" },
      styleOverrides: {
        root: {
          color: engineTokens.slate400,
          "&.Mui-checked": { color: engineTokens.brand },
        },
      },
    },
    MuiRadio: {
      defaultProps: { color: "primary" },
    },
    MuiSwitch: {
      defaultProps: { color: "primary" },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: engineTokens.radiusSm,
          fontWeight: 500,
        },
        sizeSmall: { height: 20, fontSize: "0.6875rem" },
        outlined: { borderColor: engineTokens.border },
        colorWarning: {
          backgroundColor: alpha(engineTokens.warning, 0.15),
          color: "#92400E",
          borderColor: alpha(engineTokens.warning, 0.5),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: {
          height: 3,
          borderRadius: 1.5,
          backgroundColor: engineTokens.brand,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          color: engineTokens.textSecondary,
          "&.Mui-selected": { color: engineTokens.brand },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: engineTokens.radiusMd,
          backgroundColor: engineTokens.bgPaper,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: engineTokens.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: engineTokens.borderStrong,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: engineTokens.brand,
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": { color: engineTokens.brand },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: "0.875rem" },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: engineTokens.border },
      },
    },
    MuiAccordion: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${engineTokens.border}`,
          borderRadius: engineTokens.radiusMd,
          backgroundColor: engineTokens.bgPaper,
          "&:before": { display: "none" },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: engineTokens.slate900,
          color: "#FFFFFF",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: engineTokens.radiusSm,
          padding: "6px 8px",
          maxWidth: 320,
        },
        arrow: {
          color: engineTokens.slate900,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: engineTokens.radiusMd,
        },
        standardInfo: {
          backgroundColor: engineTokens.brandSofter,
          color: engineTokens.textPrimary,
          "& .MuiAlert-icon": { color: engineTokens.brand },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: engineTokens.brandSofter,
            "&:hover": { backgroundColor: engineTokens.brandSoft },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: engineTokens.slate50 },
          "&.Mui-selected": {
            backgroundColor: engineTokens.brandSofter,
            "&:hover": { backgroundColor: engineTokens.brandSoft },
          },
        },
      },
    },
  },
});
